import React, { useState } from 'react';
import {
  AppBar,
  Box,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  ListSubheader,
  alpha,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  AccountCircle as AccountCircleIcon,
  Logout as LogoutIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import apiService from '../services/api';
import { SIDEBAR_BRAND_BG } from '../theme/dashboardBrand';
import Iconify, { sidebarIcon } from './Iconify';

const drawerWidth = 280;
const APP_BAR_MOBILE = 64;
const APP_BAR_DESKTOP = 92;
const SHERLOOK_LOGO_SRC = `${process.env.PUBLIC_URL}/assets/sherlook-logo.svg`;

type MenuItemConfig = {
  label: string;
  icon: React.ReactNode;
  path: string;
};

type MenuSection = {
  title: string;
  items: MenuItemConfig[];
};

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const navigate = useNavigate();
  const location = useLocation();

  const user = apiService.getCurrentUser();
  const userDisplayName =
    [user?.first_name, user?.last_name].filter(Boolean).join(' ').trim() || user?.email || 'Admin';

  const menuSections: MenuSection[] = [
    {
      title: 'Pilotage',
      items: [
        { label: 'Tableau de bord', icon: sidebarIcon('dashboard'), path: '/' },
        { label: 'Monitoring live', icon: sidebarIcon('statistics'), path: '/monitoring' },
        { label: 'Perf. & erreurs', icon: sidebarIcon('performance'), path: '/monitoring/performance' },
        { label: 'Alertes perf.', icon: sidebarIcon('performanceAlerts'), path: '/monitoring/alerts' },
      ],
    },
    {
      title: 'Partenaires',
      items: [
        { label: 'Partenaires', icon: sidebarIcon('providers'), path: '/providers' },
        { label: 'Menu Partenaires', icon: sidebarIcon('partnerNav'), path: '/providers/nav-order' },
        { label: 'Types de Partenaires', icon: sidebarIcon('providerTypes'), path: '/provider-types' },
        { label: 'Utilisateurs Partenaires', icon: sidebarIcon('providerUsers'), path: '/provider-users' },
      ],
    },
    {
      title: 'Utilisateurs & contenu',
      items: [
        { label: 'Utilisateurs', icon: sidebarIcon('users'), path: '/users' },
        { label: 'Catégories', icon: sidebarIcon('categories'), path: '/categories' },
        { label: 'Annonces', icon: sidebarIcon('announcements'), path: '/announcements' },
        { label: 'Matchings', icon: sidebarIcon('matchings'), path: '/matchings' },
        { label: 'Alertes moderation image', icon: sidebarIcon('imageModeration'), path: '/image-moderation-alerts' },
      ],
    },
    {
      title: 'Livraison',
      items: [
        { label: 'Stripe / Suivi colis', icon: sidebarIcon('stripe'), path: '/stripe' },
        { label: 'Tarifs livraison', icon: sidebarIcon('stripe'), path: '/delivery-pricing' },
        { label: 'Transports par ville', icon: sidebarIcon('cityTransports'), path: '/city-transports' },
      ],
    },
    {
      title: 'Accueil site',
      items: [
        { label: 'Avis Google accueil', icon: sidebarIcon('googleReviews'), path: '/home-google-reviews' },
        { label: 'Annonces fictives accueil', icon: sidebarIcon('homeTicker'), path: '/home-ticker-announcements' },
        { label: 'Couleurs perdu / trouvé', icon: sidebarIcon('announcementTheme'), path: '/home-announcement-theme' },
        { label: 'Splash screen accueil', icon: sidebarIcon('homeTicker'), path: '/home-splash-screen' },
        { label: 'Interface site public', icon: sidebarIcon('settings'), path: '/frontend-ui-settings' },
      ],
    },
    {
      title: 'Technique',
      items: [
        { label: 'Messages messagerie', icon: sidebarIcon('settings'), path: '/messaging-settings' },
        { label: 'Labo QA', icon: sidebarIcon('settings'), path: '/qa-lab' },
        { label: 'Emails satisfaction', icon: sidebarIcon('settings'), path: '/satisfaction-emails' },
        { label: 'Redirections URLs', icon: sidebarIcon('urlRedirects'), path: '/url-redirects' },
        { label: 'Logs Symfony', icon: sidebarIcon('symfonyLogs'), path: '/symfony-logs' },
        { label: 'PHP Info', icon: sidebarIcon('phpInfo'), path: '/phpinfo' },
        { label: 'Paramètres', icon: sidebarIcon('settings'), path: '/settings' },
      ],
    },
  ];

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    await apiService.logout();
    navigate('/login');
    handleMenuClose();
  };

  const isActive = (path: string) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    return location.pathname === path || location.pathname.startsWith(`${path}/`);
  };

  const drawer = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box
        sx={{
          px: 2,
          py: 2.5,
          bgcolor: SIDEBAR_BRAND_BG,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          borderBottom: '1px solid rgba(15, 23, 42, 0.06)',
          minHeight: { xs: 56, sm: 72 },
          gap: 0.5,
        }}
      >
        <Box
          component="img"
          src={SHERLOOK_LOGO_SRC}
          alt="Sherlook"
          sx={{
            width: 'auto',
            maxWidth: '92%',
            height: 'auto',
            maxHeight: 52,
            objectFit: 'contain',
            display: 'block',
          }}
        />
        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, letterSpacing: '0.04em' }}>
          Super Admin
        </Typography>
      </Box>

      <List sx={{ flex: 1, py: 1, overflowY: 'auto' }}>
        {menuSections.map((section) => (
          <Box key={section.title} component="li" sx={{ listStyle: 'none' }}>
            <ListSubheader
              disableSticky
              sx={{
                bgcolor: 'transparent',
                lineHeight: 2.2,
                fontSize: '0.7rem',
                fontWeight: 800,
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
                color: 'text.disabled',
              }}
            >
              {section.title}
            </ListSubheader>
            {section.items.map((item) => {
              const selected = isActive(item.path);
              return (
                <ListItem key={item.path} disablePadding>
                  <ListItemButton
                    selected={selected}
                    onClick={() => {
                      navigate(item.path);
                      if (isMobile) {
                        setMobileOpen(false);
                      }
                    }}
                  >
                    <ListItemIcon sx={{ color: selected ? 'primary.main' : 'text.secondary' }}>
                      {item.icon}
                    </ListItemIcon>
                    <ListItemText
                      primary={item.label}
                      sx={{
                        '& .MuiListItemText-primary': {
                          fontWeight: selected ? 800 : 600,
                          color: selected ? 'text.primary' : 'text.secondary',
                          fontSize: '0.92rem',
                        },
                      }}
                    />
                  </ListItemButton>
                </ListItem>
              );
            })}
          </Box>
        ))}
      </List>

      <Box
        sx={{
          px: 2,
          py: 2,
          mt: 'auto',
          borderTop: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 0.75,
          flexShrink: 0,
        }}
      >
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ fontSize: '0.68rem', letterSpacing: '0.06em', textTransform: 'uppercase' }}
        >
          Powered by
        </Typography>
        <Box
          component="img"
          src={SHERLOOK_LOGO_SRC}
          alt="Sherlook"
          sx={{
            width: 'auto',
            maxWidth: '88%',
            height: 30,
            objectFit: 'contain',
            display: 'block',
          }}
        />
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'background.default' }}>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          width: { md: `calc(100% - ${drawerWidth}px)` },
          ml: { md: `${drawerWidth}px` },
          boxShadow: 'none',
          backdropFilter: 'blur(6px)',
          WebkitBackdropFilter: 'blur(6px)',
          bgcolor: alpha(theme.palette.background.default, 0.72),
          color: 'text.primary',
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Toolbar sx={{ minHeight: { xs: APP_BAR_MOBILE, md: APP_BAR_DESKTOP }, px: { xs: 1.5, md: 3 } }}>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { md: 'none' }, color: 'text.primary' }}
          >
            <Iconify icon="eva:menu-2-fill" width={24} height={24} />
          </IconButton>
          <Typography
            variant="h6"
            noWrap
            component="div"
            sx={{
              flexGrow: 1,
              minWidth: 0,
              fontSize: { xs: '0.95rem', sm: undefined },
            }}
          >
            Administration Sherlook
          </Typography>
          <Box display="flex" alignItems="center" gap={{ xs: 0.5, sm: 1.5 }} sx={{ flexShrink: 0 }}>
            <Typography variant="body2" sx={{ mr: { xs: 0, sm: 1 }, display: { xs: 'none', lg: 'block' } }} noWrap>
              {userDisplayName}
            </Typography>
            <IconButton
              size="large"
              aria-label="account of current user"
              aria-controls="menu-appbar"
              aria-haspopup="true"
              onClick={handleMenuOpen}
              color="inherit"
              sx={{ color: 'text.primary' }}
            >
              <Avatar sx={{ width: 32, height: 32 }}>
                <AccountCircleIcon />
              </Avatar>
            </IconButton>
          </Box>
        </Toolbar>
      </AppBar>

      <Box component="nav" sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }} aria-label="navigation">
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              bgcolor: 'background.default',
              borderRight: '1px solid',
              borderColor: 'divider',
            },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              bgcolor: 'background.default',
              borderRight: '1px solid',
              borderColor: 'divider',
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 2, md: 3 },
          width: { md: `calc(100% - ${drawerWidth}px)` },
          maxWidth: '100%',
          overflowX: 'hidden',
          pt: { xs: `${APP_BAR_MOBILE + 16}px`, md: `${APP_BAR_DESKTOP + 24}px` },
          pb: { xs: 3, md: 5 },
        }}
      >
        {children}
      </Box>

      <Menu
        id="menu-appbar"
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem
          onClick={() => {
            handleMenuClose();
            navigate('/profile');
          }}
        >
          <ListItemIcon>
            <AccountCircleIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Mon profil</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem onClick={() => void handleLogout()}>
          <ListItemIcon>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Déconnexion</ListItemText>
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default Layout;
